import { Button, Checkbox, FormControlLabel, Stack } from "@mui/material";
import { Message as MessageType } from "../../../../apis/copilot/types";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import { transformToTree } from "../../../filters/TreeSelect/utils";
import { TreeView } from "../../../filters/TreeSelect/TreeView/TreeView";
import { useState } from "react";
import { TreeViewBaseItem } from "@mui/x-tree-view";

interface Props {
  choices: MessageType["payload"]["choices"];
  onInsert: (selectedItems: string[]) => void;
  target: string;
}

export const TreeMultiSelect: React.FC<Props> = ({
  choices,
  onInsert,
  target,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const { spendCategories } = useTaxonomyContext();
  const [isDisabled, setIsDisabled] = useState(false);

  const filteredSpendCategories = spendCategories.filter(({ id }) =>
    (choices || []).find((choice) => choice.id.toLocaleUpperCase() === id)
  );

  const spendCategoriesWithRoots = filteredSpendCategories.map(
    (spendCategory) => ({
      ...spendCategory,
      parentId: filteredSpendCategories.some(
        ({ id }) => spendCategory.parentId === id
      )
        ? spendCategory.parentId
        : "0",
    })
  );

  const items = transformToTree(
    spendCategoriesWithRoots,
    (category) => category.name
  );

  const allSelected = items.every((item) =>
    selectedItems.find((id) => id === item.id)
  );

  const addTooltipsToItems = (
    items: TreeViewBaseItem[]
  ): TreeViewBaseItem[] => {
    return items.map((item) => ({
      ...item,
      tooltip: choices?.find(
        (choice) => choice.id.toLocaleUpperCase() === item.id
      )?.reason,
      children: item.children ? addTooltipsToItems(item.children) : [],
    }));
  };

  const itemsWithTooltips = addTooltipsToItems(items);

  const isItemDisabled = ({ id }: TreeViewBaseItem) => {
    const parentId = spendCategories.find(
      (spendCategory) => spendCategory.id === id
    )?.parentId;
    return (!!parentId && selectedItems.includes(parentId)) || isDisabled;
  };

  const handleOnSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(items.map(({ id }) => id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleOnSelectionChange = (selectedIds: string[]) => {
    const updatedIds = spendCategories
      .filter(
        (spendCategory) =>
          selectedIds.includes(spendCategory.id) &&
          !selectedIds.includes(spendCategory.parentId)
      )
      .map(({ id }) => id);

    setSelectedItems(updatedIds);
  };

  return (
    <>
      <Stack direction="column" id="copilot-tree-checkboxes-container">
        <FormControlLabel
          key="select-all"
          control={
            <Checkbox
              id="copilot-select-all-checkbox"
              checked={allSelected}
              onChange={handleOnSelectAll}
            />
          }
          label="Select All"
          disabled={isDisabled}
        />
        <TreeView
          items={itemsWithTooltips}
          selectedItems={selectedItems}
          onSelectionChange={handleOnSelectionChange}
          isItemDisabled={isItemDisabled}
        />
      </Stack>

      <Button
        id="copilot-merge-button"
        variant="outlined"
        color="primary"
        disabled={selectedItems.length === 0 || isDisabled}
        onClick={() => {
          setIsDisabled(true);
          onInsert(selectedItems);
        }}
      >
        {`merge${isDisabled ? "d" : ""} into ${target}`}
      </Button>
    </>
  );
};
